import React, { useState, useEffect, useRef } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import CircularProgress from "@mui/material/CircularProgress";
import styled from "styled-components";

const StyledButton = styled(Button)(() => ({
    ":hover": {
        backgroundColor: "rgba(255, 255, 255, 0.12)",
    },
}));

const StatusBox = styled(Box)(({ theme, open }) => ({
  maxWidth: open ? '200px' : '0px',
  overflow: 'hidden',
  whiteSpace: 'nowrap',
  transition: 'max-width 1s ease-in-out, padding 1s ease-in-out, border-color 0.2s ease-in-out',
  borderRadius: '1px',
  border: '1px solid',
  borderColor: open ? 'green' : 'transparent',
  padding: open ? '4px 8px 4px' : '4px 0px 4px',
}));

export default function SaveButton({ title, clicked, disabled, children, status, statusColor }) {
    const disabledIcon = React.cloneElement(children, {
        style: { fill: "grey" },
    });
    
    const [showStatus, setShowStatus] = useState(false); // render first
    const [isOpen, setIsOpen] = useState(false); // show box after render
    const [currentStatus, setCurrentStatus] = useState("");
    const [tooltipOpen, setTooltipOpen] = useState(false);
    const timerRef = useRef(null);
    const hasStatus = status && status !== "\u00A0";


    const closeStatusBox = () => {
        setIsOpen(false);
        setTimeout(() => setShowStatus(false), 1000);
    };


    useEffect(() => {
        if (timerRef.current) {
            clearTimeout(timerRef.current);
            timerRef.current = null;
        }

        if (hasStatus) {
            setShowStatus(true);
            setCurrentStatus(status);
            setTimeout(() => setIsOpen(true), 500);
            if (status === "SAVED!") {
                timerRef.current = setTimeout(() => {
                    closeStatusBox();
                }, 5000);
            }
        } else {
            closeStatusBox();
        }

        return () => {
            if (timerRef.current) {
                clearTimeout(timerRef.current);
            }
        };
    }, [status, hasStatus]);


    const handleButtonClick = (e) => {
        setTooltipOpen(false);
        if (clicked) {
            clicked(e);
        }
    };


    return (
        <Box display="flex" alignItems="center">
            <Tooltip 
                title={title} 
                arrow 
                disableHoverListener={disabled}
                open={tooltipOpen}
                onOpen={() => setTooltipOpen(true)}
                onClose={() => setTooltipOpen(false)}
            >
                <Box
                    minWidth="32px"
                    minHeight="32px"
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    paddingLeft="2px"
                    paddingRight="2px"
                    position="relative"
                >
                    {/* show CircularProgress if status is "Saving", else show button */}
                    {status === "Saving" ? (
                        <CircularProgress
                            size={39}
                            sx={{
                                color: "green",
                                position: "absolute",
                                zIndex: 1,
                            }}
                        />
                    ) : (
                        <StyledButton
                            onClick={handleButtonClick}
                            disabled={disabled}
                            style={{
                                maxWidth: "39px",
                                maxHeight: "39px",
                                minWidth: "39px",
                                minHeight: "39px",
                            }}
                            aria-label="button"
                        >
                            {disabled ? disabledIcon : children}
                        </StyledButton>
                    )}
                </Box>
            </Tooltip>

            {/* status box with border and transitions */}
            {showStatus && (
                <StatusBox open={isOpen}>
                    <Typography
                        variant="button"
                        color={statusColor}
                    >
                        {currentStatus}
                    </Typography>
                </StatusBox>
            )}
        </Box>
    );
}